/**
 * Web Component entry point for the Wallet widget.
 *
 * Importing this module registers the <legion-wallet> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/wallet-web-component';
 *
 * // Then use in HTML:
 * // <legion-wallet user-id="123" api-key="key" />
 * ```
 */

import { WALLET_THEMING_PROP_TO_PARAM } from "./types";
import {
  attachAuthBridge,
  type AuthBridgeHandle,
  AUTH_BRIDGE_OBSERVED_ATTRS,
} from "../shared/auth-bridge";

const WALLET_CSS_ATTRIBUTES = Object.values(WALLET_THEMING_PROP_TO_PARAM);

const TOKEN_ATTRS = new Set<string>(AUTH_BRIDGE_OBSERVED_ATTRS);

class LegionWalletElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "user-id",
      "theme",
      "show-balance",
      "show-history",
      "base-url",
      "height",
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
      ...WALLET_CSS_ATTRIBUTES,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.baseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue === newValue) return;
    // Token attribute changes don't need a full iframe rebuild — just push
    // the new token via postMessage. Re-rendering would tear down state in
    // the embedded widget for no reason. See plan §4.2.
    if (TOKEN_ATTRS.has(name) && this.iframe) {
      this.authBridge?.postCurrentToken();
      return;
    }
    this.render();
  }

  private get userId(): string | null {
    return this.getAttribute("user-id");
  }

  private get apiKey(): string | null {
    // Resolved through the bridge so parent-postMessage tokens win, with the
    // `auth-token` attribute next and the legacy `api-key` last.
    return this.authBridge?.currentToken() ?? null;
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get showBalance(): boolean {
    return this.getAttribute("show-balance") !== "false";
  }

  private get showHistory(): boolean {
    return this.getAttribute("show-history") !== "false";
  }

  private get baseUrl(): string {
    return this.getAttribute("base-url") || window.location.origin;
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "500";
  }

  render(): void {
    if (!this.shadowRoot) return;

    if (!this.userId) {
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#dc3545;background:#f8f9fa;border:1px solid #dee2e6;border-radius:8px;">User ID is required</div>`;
      return;
    }

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/wallet", this.baseUrl);

    url.searchParams.set("userId", this.userId);
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("showBalance", String(this.showBalance));
    url.searchParams.set("showHistory", String(this.showHistory));

    if (this.apiKey) {
      url.searchParams.set("apiKey", this.apiKey);
    }

    WALLET_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    const heightValue = this.widgetHeight.replace("px", "");
    iframe.src = url.toString();
    iframe.width = "100%";
    iframe.height = heightValue;
    iframe.frameBorder = "0";
    iframe.scrolling = "auto";
    iframe.title = "Wallet Widget";

    iframe.onload = () => {
      // Bridge handles token resolution + targetOrigin enforcement.
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "wallet", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load wallet widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        min-height: ${heightValue}px;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        background: ${this.theme === "dark" ? "#1a1a1a" : "white"};
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-wallet")
) {
  customElements.define("legion-wallet", LegionWalletElement);
}

export { LegionWalletElement };

